import { useContext, useEffect, useMemo, useState } from "react";
import { ColumnsSettings } from "../../common/tables/ColumnsSelector/ColumnsSelector";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { TableTabsDict } from "../../common/tables/Tabs/TableTabsTypes";
import { useTabStore } from "../../common/tables/Tabs/useTabStore";
import { EntityTableTabs } from "../../common/tables/Tabs/EntityTableTabs";
import { EntityTable } from "../../common/tables/EntityTable/EntityTable";
import { LoadingWrapper } from "../../common/LoadingWrapper";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { EntityFilterIndicator } from "../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import TableView, { TableViewLabel } from "../../common/panels/TableView/TableView";
import { useEntityDetail } from "../../api/BaseEntityApi";
import { LucideIcon } from "../../common/icon/LucideIcon";
import {
  InventoryItem,
  InventoryItemFilters,
  InventoryItemFiltersTranslator,
  InventoryItemSearchTermOptions,
  hierarchyConstants,
  inventoriesConstants,
  inventoryItemsConstants,
} from "../../api/Inventories";
import { InventoryItemsFilterBar, InventoryItemFilterForm } from "./InventoryItemsFilterBar";
import { CustomType, CustomTypeFilters, customTypeConstants } from "../../api/CustomTypes/CustomTypes";
import { SessionContext } from "../../common/contexts/SessionContext";
import { getDetailLink, getEditLink, getIndexRoute } from "../../main/Routing";
import { MoreDropdown } from "../../common/buttons/MoreDropdown/MoreDropdown";
import { AlertModal } from "../../common/modals/AlertModal/AlertModal";
import { useCustomTypesEntityTable } from "../../Customization/CustomTypes/generics/useCustomTypesEntityTable";
import { NotSet } from "../../common/misc/UIconstants";
import { DateTimeRenderer } from "../../common/datetime/DateTimeFormatter";
import { GetPersons, TableArrayRenderer } from "../../common/misc/EntityRenders/EntityRenderer";
import { ExportCsvButton } from "../../common/tables/ExportCsvButton/ExportCsvButton";
import { toUppercase } from "../../common/helperfunctions/stringFunctions";
import { LinkButton } from "../../common/buttons/LinkButton/LinkButton";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  getSortStateArray,
  UseEntityTableDefaultProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
  UseITypedEntityTableProps,
} from "../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { EntityTableProps } from "../../common/entity/EntityInterfaces";
import { EntityTableCloneButton } from "../../common/entity/entityComponents/EntityTableCloneButton";
import { EntityTableSoftDeletableButton } from "../../common/entity/entityComponents/EntityTableSoftDeletableButton";
import { EntityTableRestoreButton } from "../../common/entity/entityComponents/EntityTableRestoreButton";
import { EntityTableEditButton } from "../../common/entity/entityComponents/EntityTableEditButton";
import { GenericEntityTableComplianceDropdown } from "../../Entities/GenericComponents/common/GenericEntityTable";
import SearchInputWithOptions from "../../common/forms/SearchInputWithOptions/SearchInputWithOptions";
import { NestedDropdown } from "../../common/buttons/NestedDropdown/NestedDropdown";
import { ExportPdfButton } from "../../common/tables/ExportPdfButton/ExportPdfButton";
import { CustomTypeLabelRenderer } from "../../Customization/CustomTypes/generics/CustomTypeRenderer";
import { EntityOrderBy, InferSortByOptions, ISearchTermParametersDefaults } from "../../api/GenericTypes";

export const switchInventoryItemsDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<InventoryItemFilters>>[] | null | undefined
): SortState<InferSortByOptions<InventoryItemFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;

  return getSortStateArray(sortState, (sort) => {
    switch (sort?.property) {
      case "INVENTORY_NAME":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-inventoryName",
          property: sort.property,
        };
      default:
        return {
          sortDirection: "ASC",
          headerId: "default-name",
          property: "NAME",
        };
    }
  });
};
export const defaultInventoryItemsFilters: InventoryItemFilters = {
  sortBy: [{ property: "NAME", sortDirection: "ASC" }],
  ...ISearchTermParametersDefaults,
};

export const useInventoryItemsTableDefaults = ({ fieldLabels }: UseEntityTableDefaultProps<"inventoryItems">) => {
  const { customTypeId } = useParams<{ customTypeId: string }>();
  const {
    data: customType,
    status,
    fetchStatus,
    error,
  } = useEntityDetail<CustomType, CustomTypeFilters>(customTypeConstants.resource, +(customTypeId ?? 0), undefined, {
    enabled: !!customTypeId,
  });

  const defaults: ColumnsSettings<InventoryItem> = useMemo(
    () => ({
      "default-id": { pos: 0, active: false, header: fieldLabels.id, property: "id" },
      "default-name": { pos: 1, active: true, header: fieldLabels.name, property: "name" },
      "default-inventoryName": {
        pos: 2,
        active: true,
        header: fieldLabels.rootCustomType,
        property: "rootCustomType.inventoryName",
      },
      "default-customType": { pos: 3, active: true, header: hierarchyConstants.childType, property: "customType" },
      "default-projects": { pos: 4, active: true, header: fieldLabels.projects, property: "projects" },
      "default-enteredOn": { pos: 5, active: true, header: fieldLabels.enteredOn, property: "enteredOn" },
      "default-enteredBy": { pos: 6, active: true, header: fieldLabels.enteredBy, property: "enteredBy" },
      "default-modifiedOn": {
        pos: 7,
        active: true,
        header: fieldLabels.modifiedOn,
        property: "modifiedOn",
      },
      "default-modifiedBy": {
        pos: 8,
        active: true,
        header: fieldLabels.modifiedBy,
        property: "modifiedBy",
      },
    }),
    [
      fieldLabels.enteredBy,
      fieldLabels.enteredOn,
      fieldLabels.id,
      fieldLabels.modifiedBy,
      fieldLabels.modifiedOn,
      fieldLabels.name,
      fieldLabels.projects,
      fieldLabels.rootCustomType,
    ]
  );

  const tabStoreDefaults = useMemo(() => {
    let def: TableTabsDict<InventoryItem, InventoryItemFilters, InventoryItemFilterForm> = {
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultInventoryItemsFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: {},
          sidebarFilters: {},
        },
      },
      trash: {
        tabId: "trash",
        type: "fixed",
        label: "Trash",
        title: "Trash",
        icon: "trash",
        align: "left",
        xPos: 2,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultInventoryItemsFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: { isSoftDeleted: true },
          sidebarFilters: {},
        },
      },
    };
    if (!!customType) {
      def["default"] = {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {
            "default-inventoryName": {
              pos: 3,
              active: false,
              header: fieldLabels.rootCustomType,
              property: "rootCustomType.inventoryName",
            },
            "default-customType": {
              pos: 4,
              active: false,
              header: hierarchyConstants.childType,
              property: "customType",
            },
          },
          columnWidths: {},
          filters: defaultInventoryItemsFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: { customTypeIds: [customType.id] },
          sidebarFilters: { customTypeIds: [customType] },
        },
      };
      def["trash"] = {
        tabId: "trash",
        type: "fixed",
        label: "Trash",
        title: "Trash",
        icon: "trash",
        align: "left",
        xPos: 2,
        settings: {
          columnSettings: {
            "default-inventoryName": {
              pos: 3,
              active: false,
              header: fieldLabels.rootCustomType,
              property: "rootCustomType.inventoryName",
            },
            "default-customType": {
              pos: 4,
              active: false,
              header: hierarchyConstants.childType,
              property: "customType",
            },
          },
          columnWidths: {},
          filters: defaultInventoryItemsFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: { isSoftDeleted: true, customTypeIds: [customType.id] },
          sidebarFilters: { customTypeIds: [customType] },
        },
      };
    }
    return def as TableTabsDict<InventoryItem, InventoryItemFilters, InventoryItemFilterForm>;
  }, [customType, fieldLabels.rootCustomType]);

  return { defaults, tabStoreDefaults, status, fetchStatus, error, customType };
};

export const useInventoryItemsTableColumns = ({
  entityConstants,
  fieldLabels,
  defaults,
  filters,
  sort,
  setSort,
  customType,
}: UseITypedEntityTableProps<"inventoryItems"> & { customType?: CustomType }) => {
  // Custom Fields
  const { columnSettings, customFieldHeaders } = useCustomTypesEntityTable({
    defaultColumnSettings: defaults,
    entityType: "InventoryItem",
    customTypeIds: customType?.id ? [+customType.id] : filters?.customTypeIds ?? undefined,
    defaultActive: customType ? !!customType.id : true,
    sort,
    setSort,
  });

  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<InventoryItem> = [
      {
        id: "default-id",
        Header: fieldLabels.id,
        accessor: (row) => <span style={{ color: "var(--gray-400)" }}>{row.id}</span>,
        width: 120,
        minWidth: 120,
        align: "center",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        Header: fieldLabels.name,
        id: "default-name",
        accessor: (row) => (
          <div
            style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden", alignItems: "center" }}
          >
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                <LucideIcon name={entityConstants.icon} color={"var(--primary)"} /> {row.name}
              </span>
            </div>
          </div>
        ),
        minWidth: 150,
        width: 250,
        align: "left",
        ...getAsyncSortingFn("NAME", sort, setSort),
      },
      {
        Header: fieldLabels.rootCustomType,
        id: "default-inventoryName",
        accessor: (row) => (
          // <CustomTypeLabelRenderer
          //   entity={row}
          //   propertyOverride={row.rootCustomType?.inventoryName}
          //   iconOverride={inventoriesConstants.icon}
          // />
          <div
            style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden", alignItems: "center" }}
          >
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                <LucideIcon name={inventoriesConstants.icon} color={"var(--primary)"} />{" "}
                {row.rootCustomType?.inventoryName ?? NotSet}
              </span>
            </div>
          </div>
        ),
        minWidth: 150,
        width: 250,
        align: "left",
        ...getAsyncSortingFn("INVENTORY_NAME", sort, setSort),
      },
      {
        Header: hierarchyConstants.childType,
        id: "default-customType",
        accessor: (row) => (
          <CustomTypeLabelRenderer entity={row} iconOverride={inventoriesConstants.icon} />
          // <div
          //   style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden", alignItems: "center" }}
          // >
          //   <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
          //     <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
          //       <LucideIcon name={inventoriesConstants.icon} color={"var(--primary)"} />{" "}
          //       {row.customType?.name ?? NotSet}
          //     </span>
          //   </div>
          // </div>
        ),
        minWidth: 150,
        width: 250,
        align: "left",
        ...getAsyncSortingFn("CUSTOM_TYPE", sort, setSort),
      },
      {
        Header: fieldLabels.projects,
        id: "default-projects",
        accessor: (row) => <TableArrayRenderer values={row.projects}>{(value) => value.name}</TableArrayRenderer>,
        width: 200,
        align: "left",
      },
      {
        id: "default-enteredOn",
        Header: fieldLabels.enteredOn,
        accessor: (row) => <DateTimeRenderer date={row.enteredOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_ON", sort, setSort),
      },
      {
        id: "default-enteredBy",
        Header: fieldLabels.enteredBy,
        accessor: (row) => <GetPersons persons={[row.enteredBy]} createLinks={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_BY", sort, setSort),
      },
      {
        id: "default-modifiedOn",
        Header: fieldLabels.modifiedOn,
        accessor: (row) => <DateTimeRenderer date={row.modifiedOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_ON", sort, setSort),
      },
      {
        id: "default-modifiedBy",
        Header: fieldLabels.modifiedBy,
        accessor: (row) => <GetPersons persons={[row.modifiedBy]} createLinks={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_BY", sort, setSort),
      },
    ];
    // Add custom field headers
    headers = headers.concat(customFieldHeaders);
    return headers;
  }, [
    customFieldHeaders,
    entityConstants.icon,
    fieldLabels.enteredBy,
    fieldLabels.enteredOn,
    fieldLabels.id,
    fieldLabels.modifiedBy,
    fieldLabels.modifiedOn,
    fieldLabels.name,
    fieldLabels.projects,
    fieldLabels.rootCustomType,
    setSort,
    sort,
  ]);
  return { columns, columnSettings };
};
export const InventoryItemsTableWrapper = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
  entityTypeId,
}: EntityTableProps<"inventoryItems">) => {
  const { defaults, tabStoreDefaults, status, fetchStatus, error, customType } = useInventoryItemsTableDefaults({
    fieldLabels,
  });

  return (
    <LoadingWrapper status={status} fetchStatus={fetchStatus} error={error}>
      <InventoryItemsTable
        entityTypeId={entityTypeId}
        customType={customType}
        defaults={defaults}
        tabStoreDefaults={tabStoreDefaults}
        entityApi={entityApi}
        entityConstants={entityConstants}
        fieldLabels={fieldLabels}
        permissions={permissions}
        routes={routes}
      />
    </LoadingWrapper>
  );
};

export const InventoryItemsTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
  entityTypeId,
  customType,
  defaults,
  tabStoreDefaults,
}: {
  customType?: CustomType;
  defaults: ColumnsSettings<InventoryItem>;
  tabStoreDefaults: TableTabsDict<InventoryItem, InventoryItemFilters, InventoryItemFilterForm>;
} & EntityTableProps<"inventoryItems">) => {
  const history = useHistory();
  const { route, session } = useContext(SessionContext);
  const {
    bulkRestoreMutationAsync,
    bulkDeleteMutationAsync,
    isLoadingBulkDeleteMutation,
    isLoadingBulkRestoreMutation,
  } = entityApi;

  const { selection, resultsCount, selectionPermissions, onCountChange, onSelectionChange, onSelectionPermissions } =
    useGenericVirtualizedTable<InventoryItem>();

  const {
    filters,
    sidebarFilters,
    forcedFilters,
    columnSetting,
    columnWidths,
    customTabs,
    fixedTabs,
    temporaryTabs,
    dispatchTabStore,
    currentTab,
    tabsLoading,
    tabsModified,
  } = useTabStore<InventoryItem, InventoryItemFilters, InventoryItemFilterForm>({
    resource: entityConstants.resource,
    defaults: tabStoreDefaults,
    keyOverride: customType
      ? `tableTabs-inventories-${customType.id}`
      : `tableTabs-${inventoryItemsConstants.resource}`,
  });

  const { functionRef, sort, setSort, searchValue, setSearchValue, onTabChange, debouncedSearchValue } =
    useGenericVirtualizedTableTabs({
      currentTab,
      tabsLoading,
      filters,
      switchSortState: switchInventoryItemsDefaultSortState,
      dispatchTabStore,
    });

  const { columns, columnSettings } = useInventoryItemsTableColumns({
    entityConstants,
    fieldLabels,
    defaults,
    filters,
    sort,
    setSort,
    customType,
  });

  // TODO: remove once generic in tab store
  const queryTypeId = new URLSearchParams(useLocation().search).get("type");
  useEffect(() => {
    if (!tabsLoading && queryTypeId === "default") {
      (async () => await onTabChange("default"))();
    } else if (!tabsLoading && queryTypeId && +queryTypeId) {
      (async () => await onTabChange(`inventory-id-${+queryTypeId}`))();
    }
  }, [history, onTabChange, queryTypeId, tabsLoading]);

  const memoizedFilters = useMemo(() => {
    return { ...filters, ...forcedFilters };
  }, [filters, forcedFilters]);

  const [showModal, setShowModal] = useState(false);

  return (
    <TableView>
      <TableView.Head>
        <TableView.Head.Label>
          <div className="flex align-center gap-5">
            <Link to={route(getIndexRoute(inventoriesConstants.frontendIndexRoute))} style={{ textDecoration: "none" }}>
              <div
                className="flex row-nowrap align-center gap-5"
                style={{
                  fontSize: "24px",
                  fontWeight: 600,
                  lineHeight: "24px",
                  whiteSpace: "nowrap",
                }}
              >
                <LucideIcon name={inventoriesConstants.icon} color="var(--primary)" style={{ width: 20, height: 20 }} />
                {toUppercase(inventoriesConstants.entityPlural)}
              </div>
            </Link>
            <LucideIcon name="arrow-big-right" color="var(--gray-400)" />
            <TableViewLabel
              entityConstants={entityConstants}
              resultsCount={resultsCount}
              overwriteLabel={customType?.inventoryName ?? undefined}
            />
            {!!customType && !customType.inventoryName && (
              <div className="label label-soft-info">{customType.name}</div>
            )}
          </div>
        </TableView.Head.Label>
        <TableView.Head.Controls>
          {customType && (
            <>
              {!!customType?.isHierarchyRoot && (
                <LinkButton
                  className="btn btn-default"
                  linkTo={`${route(getDetailLink(inventoriesConstants.frontendIndexRoute, customType.id))}/hierarchy`}
                  title="Show items in hierarchy"
                  disabled={!customType.id}
                >
                  <LucideIcon name="list-tree" />
                </LinkButton>
              )}
              <LinkButton
                className="btn btn-default"
                linkTo={route(getEditLink(customTypeConstants.frontendIndexRoute, customType.id))}
                title={`Edit ${inventoriesConstants.entitySingular}`}
                disabled={!session?.permissions.administer_custom_field_schemas}
              >
                <LucideIcon name="square-pen" /> Edit
              </LinkButton>
            </>
          )}

          <LinkButton
            className="btn btn-primary"
            linkTo={`${routes.getAddRoute}${customType?.id ? `?type=${customType.id}` : ""}`}
            title={`Add new ${inventoriesConstants.entitySingular}`}
            disabled={
              !!customType?.hasRestrictedAddPermission
                ? !session?.permissions.add_admin_inventory_items
                : !session?.permissions.add_inventory_items
            }
          >
            <LucideIcon name="plus" /> Add {entityConstants.entitySingular}
          </LinkButton>
        </TableView.Head.Controls>
      </TableView.Head>
      <TableView.Body>
        <TableView.Body.Sidebar minSize={20}>
          <InventoryItemsFilterBar
            initialValues={sidebarFilters}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            currentTab={currentTab}
            hideReset={currentTab !== "default"}
            hideHierarchyFilters={!!customType}
          />
        </TableView.Body.Sidebar>
        <TableView.Body.Content>
          <EntityTableTabs
            currentTab={currentTab}
            onTabChange={onTabChange}
            temporaryTabs={temporaryTabs}
            fixedTabs={fixedTabs}
            filters={filters}
            sidebarFilters={sidebarFilters}
            columnSetting={columnSetting}
            columnWidths={columnWidths}
            customTabs={customTabs}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            tabsModified={tabsModified}
          />
          <EntityTable>
            <EntityTable.Controls
              style={{
                borderTop: "0px",
                borderRadius: "0px",
              }}
            >
              <EntityFilterIndicator<InventoryItem, InventoryItemFilters>
                filters={filters}
                excludeFilters={{
                  customTypeIds: customType?.id ? () => false : (value) => !!value,
                  childrenOfParentIds: (value) => value != null,
                  searchTermIncludeIds: () => false,
                  searchTermIncludeNames: () => false,
                  searchTermIncludeUuids: () => false,
                  searchTermIncludeCustomFieldValues: () => false,
                  includeSoftDeleted: (value) => value === true,
                }}
                translatorConsts={InventoryItemFiltersTranslator}
                showAlways
              />
              <SearchInputWithOptions
                searchTermOptions={InventoryItemSearchTermOptions}
                filters={filters}
                setFilters={async (newFilters) => {
                  await dispatchTabStore({
                    type: "setTab",
                    options: { keepPrevious: true },
                    payload: { settings: { filters: { searchTerm: debouncedSearchValue, ...newFilters } } },
                  });
                }}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
              />
              {/* <SearchInput
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                placeholder={`Search ${entityConstants.entityPlural}...`}
              /> */}

              {currentTab !== "trash" && (
                <div>
                  <EntityTableEditButton
                    entityConstants={entityConstants}
                    routes={routes}
                    permissions={permissions}
                    selection={selection}
                    selectionPermissions={selectionPermissions}
                  />
                </div>
              )}
              <div>
                <MoreDropdown drop="right" btn="btn btn-ghost-secondary">
                  <li>
                    <EntityTableCloneButton
                      entityConstants={entityConstants}
                      routes={routes}
                      permissions={permissions}
                      selection={selection}
                    />
                  </li>
                  <NestedDropdown label="Share / Export" icon="share-2" disabled={selection.size === 0}>
                    <li>
                      <ExportCsvButton
                        entityConstants={entityConstants}
                        columnSettings={columnSetting}
                        filters={{
                          ids: selection.size > 0 ? Array.from(selection) : undefined,
                          ...memoizedFilters,
                          searchTerm: debouncedSearchValue,
                        }}
                        disabled={selection.size === 0}
                      />
                    </li>
                    <li>
                      <ExportPdfButton
                        entityConstants={entityConstants}
                        filters={{
                          ids: selection.size > 0 ? Array.from(selection) : undefined,
                          ...memoizedFilters,
                          searchTerm: debouncedSearchValue,
                        }}
                        disabled={selection.size === 0}
                      />
                    </li>
                  </NestedDropdown>
                  {/* Locking & Signing */}
                  <GenericEntityTableComplianceDropdown
                    entityTypeId={entityTypeId}
                    selection={selection}
                    selectionPermissions={selectionPermissions}
                    tableFunctionRef={functionRef}
                    currentTab={currentTab}
                    showSignButton
                  />

                  {/* Trash */}
                  <li className="divider" />
                  {currentTab === "trash" && (
                    <li>
                      <EntityTableRestoreButton
                        entityConstants={entityConstants}
                        selection={selection}
                        permissions={permissions}
                        selectionPermissions={selectionPermissions}
                        onClick={async () =>
                          await bulkRestoreMutationAsync({ ids: Array.from(selection) }).catch(() => {})
                        }
                        loading={isLoadingBulkRestoreMutation}
                      />
                    </li>
                  )}
                  <li>
                    <EntityTableSoftDeletableButton
                      currentTab={currentTab}
                      entityConstants={entityConstants}
                      selection={selection}
                      permissions={permissions}
                      selectionPermissions={selectionPermissions}
                      onClick={() => setShowModal(true)}
                    />
                  </li>
                </MoreDropdown>
              </div>

              <AlertModal
                type={`${currentTab === "trash" ? "danger" : "warning"}`}
                showModal={showModal}
                setShowModal={setShowModal}
                title={`${
                  selection.size === 0
                    ? `Select dataset to ${currentTab === "trash" ? "delete" : "trash"}`
                    : selection.size === 1
                    ? `${currentTab === "trash" ? "Delete" : "Trash"} the selected ${
                        inventoriesConstants.entitySingular
                      }?`
                    : `${currentTab === "trash" ? "Delete" : "Trash"} the selected ${
                        inventoriesConstants.entityPlural
                      }?`
                }`}
                description={`${
                  currentTab === "trash"
                    ? `Proceeding will permanently delete the selected ${inventoriesConstants.entityPlural}.`
                    : `Proceeding will move the selected ${inventoriesConstants.entityPlural} into trash.`
                }`}
                proceedLabel={`${currentTab === "trash" ? "Delete" : "Trash"}`}
                onProceed={async () => {
                  await bulkDeleteMutationAsync({
                    ids: Array.from(selection),
                    goBackOnSuccess: false,
                    showToast: false,
                    entityName: inventoriesConstants.entityPlural,
                    params: { deletePermanently: currentTab === "trash" },
                  }).catch((e) => {});
                  functionRef.current?.resetSelection();
                  setShowModal(false);
                }}
                loading={isLoadingBulkDeleteMutation}
              />
            </EntityTable.Controls>
            <EntityTable.Body<InventoryItem, InventoryItemFilters>
              functionRef={functionRef}
              entityConstants={entityConstants}
              filters={memoizedFilters}
              columns={columns}
              columnSelect
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={columnSettings}
              dispatchTabStore={dispatchTabStore}
              setResultsCount={onCountChange}
              onSelectionChange={onSelectionChange}
              onSelectionPermissions={onSelectionPermissions}
              showPermissionColumn
              loading={tabsLoading}
              // onRowClick={() => {}}
              // linkTo={() => "#"}
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};
